import { createNode } from '@glueapp/graph-types';

export const CreateList = createNode({
    id: 'Create List',
    name: 'Create List',
    description:
        'Creates a list by executing a function for each index from 0 to Length-1. The function receives the index and returns the item at that position.',
    aiDescription: [
        'Creates a list by calling a function N times (once per index from 0 to Length-1). Async — uses executeFunctionNode.',
        'Setup steps:',
        '1. Create a function with input port "Index" (number) and output port "Item" (any type T).',
        '2. Add this node.',
        '3. Set "Length" to the desired list size (number).',
        '4. Bind the function to the "Item Creator" port (kind: function) by SETTING its value to a function reference (not a connection): set_value <thisNodeId> "Item Creator" {"isFunctionReferenceValue":true,"functionId":"<functionId>"}.',
        'Output: "List" — an array of type T, built from each function call\'s "Item" output.',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    isAsync: true,

    inPorts: {
        Length: {
            kind: 'number',
        },
        'Item Creator': {
            kind: 'function',
            inputTypes: {
                Index: { kind: 'number' },
            },
            outputTypes: {
                Item: { kind: 'generic', identifier: 'T' },
            },
        },
    },

    outPorts: {
        List: {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
    },

    execute: async ({ inPorts, executeFunctionNode }) => {
        if (!executeFunctionNode) {
            throw new Error('Function execution not available');
        }

        const length = inPorts['Length'] as number;
        const itemCreatorValue = inPorts['Item Creator'];

        // Check if the item creator is a FunctionReferenceValue
        if (
            !itemCreatorValue ||
            typeof itemCreatorValue !== 'object' ||
            !('isFunctionReferenceValue' in itemCreatorValue) ||
            !itemCreatorValue.isFunctionReferenceValue
        ) {
            throw new Error('Item Creator must be a function reference');
        }

        const itemCreator = itemCreatorValue as any; // Type as FunctionReferenceValue

        if (!length || length < 0 || !Number.isInteger(length)) {
            return {
                output: {
                    List: [],
                },
            };
        }

        const list = [];

        for (let i = 0; i < length; i++) {
            // Execute the item creator function for each index
            const result = await executeFunctionNode(itemCreator.functionId, {
                Index: i,
            });

            list.push(result.output.Item);
        }

        return {
            output: {
                List: list,
            },
        };
    },
});
